import * as RouteService from '../../services/routes';
import {
  getRouteDetail,
  isCurrentRouteHasRequestWithoutResponse,
  getTransferRouteId,
  getRoute,
  isDefaultRoute,
  getCurrentRouteId,
  isRouteStatusSuitableForResync,
} from '../selectors/route';
import {
  formatScheduleData,
  getScheduleResults,
  isRouteSchedulingOpen,
  getOptimise,
  getDrawerTitle,
  isDowngradeRequired,
  getScheduleError,
  getEnableRetry,
  getAbortController,
} from '../selectors/schedule';
import * as ScheduleModel from '../../models/schedule';
import {
  setRouteDetail,
  saveRoute,
  unlockRoute,
  setScheduleRequired,
  lockRoute,
  resetAllTransferFromRouteEntity,
  setSaveRouteEnable,
  incrementRequestOrder,
  resetRequestOrder,
  setInitialDowngradeAttempts,
  setStops,
  hideAllNotifications,
  onResyncRoute,
} from '../reducers/route';
import { getSelectedDepot } from '../selectors/app';
import {
  OPEN_REQUESTS,
  ARE_YOU_SURE_TO_RESET,
  ARE_YOU_SURE_TO_RESET_AND_CONTINUE,
  ROUTE_WITHOUT_DELIVERY_STOPS,
  IS_NOT_REQUIRED,
  FAILED_REASON,
  ROUTE_IS_DEFAULT_ROUTE,
} from '../../constants/strings';
import { showNotification, showResyncRouteNotification } from './notifications';
import { NotificationType } from '../../features/Notifier';
import { SCHEDULE_MODAL } from '../../constants/analytics';
import { parseQuery, stringifyQuery } from '../../utils/query';
import { getErrorMessage, isValidationError } from '../../utils/errors';
import { endLoadingProcess, startLoadingProcess } from '../reducers/loader';
import { RESET_CHANGES_PROCESS } from '../../enums/loaderProcess';
import {
  scheduleRouteClose,
  scheduleRouteComplete,
  scheduleRouteError,
  scheduleRouteSetAbortController,
  scheduleRouteSetOptimise,
  scheduleRouteSetRoute,
  scheduleRouteStart,
} from '../reducers/schedule';
import { hideNotifications } from '../reducers/notifications';

const CONFIRMATION_MODAL_KEY = 'confirmation-modal-key';

const hideConfirmNotification = () => (dispatch) =>
  dispatch(hideNotifications([CONFIRMATION_MODAL_KEY]));

export const processScheduleRoute =
  (scheduleData) => async (dispatch, getState) => {
    const state = getState();
    const drawerTitle = getDrawerTitle(state);
    const depotCode = getSelectedDepot(state);
    const routeId = getCurrentRouteId(state);

    if (scheduleData.lunchDuration && !scheduleData.lunchStopNumber) {
      dispatch(
        scheduleRouteError({
          error: `${ROUTE_WITHOUT_DELIVERY_STOPS} ${drawerTitle} ${IS_NOT_REQUIRED}`,
          isRetryEnabled: false,
          isSaveEnabled: true,
        })
      );
      return null;
    }

    try {
      const abortController = new AbortController();
      dispatch(scheduleRouteSetAbortController(abortController));

      const { data } = await RouteService.scheduleRoute(
        depotCode,
        routeId,
        scheduleData,
        abortController
      );
      const isOpen = isRouteSchedulingOpen(getState());
      return isOpen ? data : null;
    } catch (error) {
      if (isRouteSchedulingOpen(getState())) {
        const isResyncRouteEnabled =
          isRouteStatusSuitableForResync(state) && isValidationError(error);
        isResyncRouteEnabled &&
          dispatch(
            showResyncRouteNotification({
              error,
              onConfirm: () => dispatch(onResyncRoute()),
            })
          );
        dispatch(
          scheduleRouteError({
            error: `${drawerTitle} ${FAILED_REASON} ${getErrorMessage(error)}`,
            isRetryEnabled: true,
            isSaveEnabled: false,
          })
        );
      }
      return null;
    }
  };

export const finishScheduleProcess = (scheduleResponse) => (dispatch) => {
  dispatch(scheduleRouteComplete(scheduleResponse));
  dispatch(setScheduleRequired(false));
  dispatch(setSaveRouteEnable(true));
  dispatch(updateRouteAfterScheduling());
};

export const getScheduleData = () => async (dispatch, getState) => {
  const state = getState();
  const scheduleData = formatScheduleData(state);
  const downgradeRequired = isDowngradeRequired(state);
  const routeDetail = getRouteDetail(state);
  const scheduleResponse = await dispatch(processScheduleRoute(scheduleData));

  if (scheduleResponse) {
    dispatch(scheduleRouteSetRoute(routeDetail));
    if (
      downgradeRequired &&
      ScheduleModel.hasServiceException(scheduleResponse?.stop)
    ) {
      dispatch(downgradeParcelsProcess(scheduleResponse));
    } else {
      dispatch(finishScheduleProcess(scheduleResponse));
    }
  }
};

export const processDowngrade = (route, scheduleResult) => async (dispatch) => {
  const stopsToDowngrade = ScheduleModel.getStopsWithServiceException(
    scheduleResult.stop
  );

  const downgradeObject = ScheduleModel.createDowngradedRoute(
    stopsToDowngrade,
    route
  );
  if (downgradeObject) {
    const formattedScheduleData = ScheduleModel.formatScheduleData(
      downgradeObject,
      true
    );

    const scheduleResponse = await dispatch(
      processScheduleRoute(formattedScheduleData)
    );
    return {
      downgradeObject,
      scheduleResponse,
    };
  }
  return {};
};

export const downgradeParcelsProcess =
  (scheduleResult) => async (dispatch, getState) => {
    const state = getState();
    const route = getRouteDetail(state);
    const DOWNGRADE_ATTEMPTS = 3;

    let downgradeEntities = [{ route, scheduleResponse: scheduleResult }];

    for (let attempt = 0; attempt < DOWNGRADE_ATTEMPTS; attempt++) {
      const { downgradeObject, scheduleResponse } = await dispatch(
        processDowngrade(
          downgradeEntities[attempt].route,
          downgradeEntities[attempt].scheduleResponse
        )
      );
      if (scheduleResponse) {
        if (!ScheduleModel.hasServiceException(scheduleResponse?.stop)) {
          dispatch(setInitialDowngradeAttempts(attempt + 1));
          dispatch(setStops(downgradeObject.stop));
          return dispatch(finishScheduleProcess(scheduleResponse));
        }
        downgradeEntities.push({ route: downgradeObject, scheduleResponse });
      } else if (scheduleResponse !== null) {
        break;
      } else {
        return;
      }
    }
    downgradeEntities.shift();
    const optimalRoute = downgradeEntities.reduce(
      (optimal, entity) =>
        ScheduleModel.getServiceExceptionTotal(optimal.scheduleResponse?.stop) >
        ScheduleModel.getServiceExceptionTotal(entity.scheduleResponse?.stop)
          ? entity
          : optimal,
      downgradeEntities[0]
    );
    if (optimalRoute) {
      dispatch(setInitialDowngradeAttempts(DOWNGRADE_ATTEMPTS));
      dispatch(setStops(optimalRoute.route.stop));
      return dispatch(finishScheduleProcess(optimalRoute.scheduleResponse));
    }
    return dispatch(finishScheduleProcess(scheduleResult));
  };

export const scheduleRoute = () => async (dispatch, getState) => {
  const state = getState();
  const drawerTitle = getDrawerTitle(state);
  const isRouteDefault = isDefaultRoute(state);
  const requestWithoutResponse = isCurrentRouteHasRequestWithoutResponse(state);

  dispatch(scheduleRouteStart());

  if (isRouteDefault) {
    return dispatch(
      scheduleRouteError({
        error: `${ROUTE_IS_DEFAULT_ROUTE}. ${drawerTitle} ${IS_NOT_REQUIRED}.`,
        isRetryEnabled: false,
        isSaveEnabled: true,
      })
    );
  }

  if (requestWithoutResponse) {
    return dispatch(
      scheduleRouteError({
        error: `${drawerTitle} ${FAILED_REASON} ${OPEN_REQUESTS}.`,
        isSaveEnabled: false,
        isRetryEnabled: false,
      })
    );
  }

  await dispatch(getScheduleData());
};

export const startScheduleRoute =
  (optimise = false) =>
  async (dispatch) => {
    const currentUserLock = await dispatch(lockRoute());

    if (!currentUserLock) {
      return;
    }
    dispatch(scheduleRouteSetOptimise(optimise));
    dispatch(scheduleRoute());
  };

export const onResetConfirm = () => (dispatch) => {
  dispatch(resetAllChanges());
  dispatch(hideConfirmNotification());
};

export const onResetTransferNotification = () => (dispatch) => {
  dispatch(
    showNotification({
      key: CONFIRMATION_MODAL_KEY,
      type: NotificationType.ACTIONS,
      message: ARE_YOU_SURE_TO_RESET_AND_CONTINUE,
      onConfirm: () => {
        dispatch(onResetConfirm());
      },
    })
  );
};

export const finishScheduleRoute = () => (dispatch, getState) => {
  const abortController = getAbortController(getState());
  abortController && abortController.abort();
  dispatch(hideConfirmNotification());
  dispatch(scheduleRouteClose());
};

export const onCloseScheduleRoute = () => (dispatch, getState) => {
  const state = getState();
  const isTransferProcessStarted = getTransferRouteId(state);
  const scheduleError = getScheduleError(state);
  const enableRetry = getEnableRetry(state);
  if (isTransferProcessStarted) {
    dispatch(incrementRequestOrder());
    scheduleError && enableRetry
      ? dispatch(onResetTransferNotification())
      : dispatch(finishScheduleRoute());
  } else {
    dispatch(finishScheduleRoute());
  }
};

export const updateRouteAfterScheduling = () => (dispatch, getState) => {
  const state = getState();
  const routeDetail = getRouteDetail(state);
  const scheduleData = getScheduleResults(state);
  const optimise = getOptimise(state);
  const updatedRouteDetail = ScheduleModel.updateRouteDetailByScheduleData(
    routeDetail,
    scheduleData,
    optimise
  );
  dispatch(setRouteDetail(updatedRouteDetail));
};

export const resetAllRouteEntity = () => (dispatch, getState) => {
  const { transferFromRouteId, transferRouteId } = getRoute(getState());
  if (transferFromRouteId && transferRouteId) {
    dispatch(resetAllTransferFromRouteEntity());
  }
  dispatch(hideAllNotifications());
  dispatch(resetRequestOrder());
  dispatch(setScheduleRequired(false));
  dispatch(setSaveRouteEnable(false));
};

export const resetAllChanges = () => async (dispatch, getState) => {
  try {
    dispatch(startLoadingProcess(RESET_CHANGES_PROCESS));
    const checksum = await dispatch(unlockRoute());
    const { originalRouteDetail } = getRoute(getState());

    dispatch(
      setRouteDetail({
        ...(originalRouteDetail.stopNumbersSet
          ? originalRouteDetail
          : {
              ...originalRouteDetail,
              originalStopsData: [],
            }),
        checksum,
        routeLockedBy: null,
      })
    );
    dispatch(resetAllRouteEntity());
    dispatch(finishScheduleRoute());
  } catch {
  } finally {
    dispatch(endLoadingProcess(RESET_CHANGES_PROCESS));
  }
};

export const onSaveAndExitClick = () => async (dispatch) => {
  const success = await dispatch(saveRoute());
  dispatch(finishScheduleRoute());

  return success;
};

export const onResetClick = () => (dispatch) => {
  dispatch(
    showNotification({
      key: CONFIRMATION_MODAL_KEY,
      type: NotificationType.ACTIONS,
      message: ARE_YOU_SURE_TO_RESET,
      confirmActionId: SCHEDULE_MODAL.CONFIRM_RESET_ALL_CHANGES,
      cancelActionId: SCHEDULE_MODAL.CANCEL_RESET_ALL_CHANGES,
      onConfirm: () => {
        dispatch(onResetConfirm());
      },
    })
  );
};

export const handleSaveAndExitClick =
  (navigate, location) => async (dispatch) => {
    // eslint-disable-next-line no-unused-vars
    const { detailVisible, routeId, ...newQuery } = parseQuery(location.search);

    const success = await dispatch(onSaveAndExitClick());

    success &&
      navigate({
        search: stringifyQuery(newQuery),
      });
  };

export const scheduleActionCreators = {
  onClose: onCloseScheduleRoute,
  onResetClick,
  onMoreChangesClick: onCloseScheduleRoute,
  onRetryClick: scheduleRoute,
};
